<template>
  <modal :name="name" :width="720" height="auto" :adaptive="true" :scrollable="true" :focus-trap="true" :clickToClose="false" @before-open="initialize" @before-close="beforeClose" @closed="onClosed" @opened="focusHeading">
    <div class="popup passenger-editor" @keydown.esc.prevent.stop="close">
      <button class="popup__close" type="button" :disabled="saving" aria-label="Close passenger editor" @click="close" />
      <h2 ref="heading" class="popup__title" tabindex="-1">{{ passenger ? editTitle : 'Link Clients' }}</h2>
      <div class="popup__body">
        <div v-if="loading" class="passenger-editor__loading"><app-spinner /></div>
        <div v-else-if="loadError" class="passenger-editor__error" role="alert">
          <span class="passenger-editor__error-icon" aria-hidden="true"><i class="pi pi-exclamation-triangle" /></span>
          <p>{{ loadError }}</p>
          <app-button theme="dark" size="small" @click.native="loadOptions">Retry</app-button>
        </div>
        <form v-else novalidate @submit.prevent="save">
          <template v-if="!passenger">
            <div v-if="noClients" class="passenger-editor__notice" role="status">
              <i class="pi pi-info-circle" aria-hidden="true" />
              <div>
                <strong>No clients available</strong>
                <p>Create a client group for this lead and add clients before linking passengers. If a group already exists, add clients to it, then reopen this window.</p>
              </div>
            </div>
            <div v-for="slot in passengers" :key="slot.id" class="passenger-editor__slot" :data-age-type="code(slot.age_type)">
              <div class="passenger-editor__slot-identity">
                <span class="passenger-editor__age">{{ label(slot.age_type) }}</span>
                <span class="passenger-editor__slot-number">Passenger #{{ slot.id }}</span>
              </div>
              <app-form-group class="passenger-editor__slot-select">
                <app-select v-model="assignments[slot.id]" :options="clients" :reduce="client => client.id" :display-field="clientLabel" :selectable="client => clientSelectable(client, slot)" :disabled="saving || completed || !canLinkClients || noClients" :aria-label="`Client for passenger #${slot.id}`" placeholder="Not linked" />
              </app-form-group>
            </div>
          </template>
          <template v-else>
            <section v-for="section in sections" :key="section.title" class="passenger-editor__section">
              <h3>{{ section.title }}</h3>
              <div class="passenger-editor__grid">
                <app-form-group v-for="field in section.fields" :key="field.key" :label="field.label" :required="required(field)">
                  <app-select v-if="field.options" v-model="draft[field.key]" :options="field.options === 'genders' ? genders : countries" :reduce="option => option.code" :disabled="saving || completed" :error="Boolean(fieldErrors[field.key])" :aria-label="field.label" :placeholder="field.label" @input="clearFieldError(field.key)">
                    <template #custom-option="option">
                      <span v-if="field.options === 'countries' && option.code" class="passenger-editor__flag fflag ff-sm" :class="`fflag-${option.code}`" aria-hidden="true" />
                      <span>{{ option.name }}</span>
                    </template>
                  </app-select>
                  <app-date-picker v-else-if="field.type === 'date'" v-model="draft[field.key]" :disabled="saving || completed" :disabled-date="date => dateDisabled(field.key, date)" :error="Boolean(fieldErrors[field.key])" :input-attr="{ 'aria-label': field.label }" :placeholder="field.label" @input-valid="clearFieldError(field.key)" @input-error="invalidDateInput(field.key)" />
                  <app-phone v-else-if="field.key === 'phone'" v-model="draft.phone" placeholder="Phone" :readonly="saving || completed" :error="Boolean(fieldErrors.phone)" @input="clearFieldError('phone')" />
                  <app-input v-else v-model="draft[field.key]" :type="field.type || 'text'" :name="field.key" :aria-label="field.label" :placeholder="field.label" :disabled="saving || completed" :maxlength="field.maxlength" :error="Boolean(fieldErrors[field.key])" @input="clearFieldError(field.key)" />
                  <template #error><div v-if="fieldErrors[field.key]">{{ fieldErrors[field.key] }}</div></template>
                </app-form-group>
              </div>
            </section>
            <app-form-group v-if="code(passenger.age_type) === 'adult' && infants.length" label="Carrying infant">
              <app-select v-model="draft.carrying_infant_passenger_id" :options="infants" :reduce="infant => infant.id" :display-field="personLabel" :disabled="saving || completed" aria-label="Carrying infant" placeholder="None" />
            </app-form-group>
          </template>
          <app-cells position="end" :indent="false" gap="12px">
            <app-button theme="dark" :disabled="saving" @click.native="close">Cancel</app-button>
            <app-button type="submit" :disabled="saving || completed || noClients || (!passenger && !canLinkClients)">{{ passenger ? 'Save passenger' : 'Save assignments' }}</app-button>
          </app-cells>
        </form>
      </div>
    </div>
  </modal>
</template>

<script>
import { getFlightsBookingClients, postFlightsBookingPassengersLinkClients, putFlightsBookingPassenger, getGenders, getCountries } from '@/http'
import { code, label, empty, rows, hasBookingSalesRole, responseError } from './booking-detail'
import { personalFields, passengerName, passengerPayload, calendarDay, todayInEAT, passengerDateErrors, passengerRequestErrors } from './booking-passengers'

const sections = [
  { title: 'Personal details',
    fields: [
      { key: 'first_name', label: 'First name', maxlength: 80 },
      { key: 'last_name', label: 'Last name', maxlength: 80 },
      { key: 'gender', label: 'Gender', options: 'genders' },
      { key: 'dob', label: 'Date of birth', type: 'date', policy: 'dateofbirth' },
      { key: 'age', label: 'Age on departure', type: 'number', policy: 'age' },
      { key: 'citizenship', label: 'Citizenship', options: 'countries', policy: 'nationality' }
    ] },
  { title: 'Travel document',
    fields: [
      { key: 'passport_number', label: 'Passport number', maxlength: 80, policy: 'documentnumber' },
      { key: 'passport_country', label: 'Issuing country', options: 'countries', policy: 'documentissueingcountry' },
      { key: 'passport_expiration_date', label: 'Passport expiry', type: 'date', policy: 'documentexpiry' }
    ] },
  { title: 'Contact details',
    fields: [
      { key: 'phone', label: 'Phone', type: 'tel', maxlength: 64, policy: 'phone' },
      { key: 'email', label: 'Email', type: 'email', maxlength: 254 }
    ] }
]

export default {
  name: 'BookingPassengerModal',
  props: {
    name: { type: String, required: true },
    booking: { type: Object, required: true },
    passengers: { type: Array, required: true },
    passenger: { type: Object, default: null },
    policy: { type: Object, default: () => ({}) },
    completed: { type: Boolean, default: false }
  },
  data() {
    return { sections, assignments: {}, draft: {}, clients: [], genders: [], countries: [], loading: true, saving: false, loadError: '', fieldErrors: {}, dateInputErrors: {}, sessionId: 0, opener: null }
  },
  computed: {
    canLinkClients() { return Boolean(this.booking.lead) && hasBookingSalesRole(this.$store.state.userGroups) },
    noClients() { return !this.passenger && this.canLinkClients && !this.loading && !this.loadError && !this.clients.length },
    editTitle() { return this.passenger.first_name || this.passenger.last_name ? 'Edit passenger' : 'Add passenger details' },
    infants() { return this.passengers.filter(person => code(person.age_type) === 'infant') }
  },
  beforeDestroy() { this.sessionId++ },
  methods: {
    code,
    label,
    dateDisabled(field, date) {
      const value = calendarDay(date)
      return field === 'dob' ? value >= todayInEAT() : value <= todayInEAT()
    },
    clearFieldError(field) {
      this.$delete(this.fieldErrors, field)
      this.$delete(this.dateInputErrors, field)
    },
    invalidDateInput(field) {
      const message = field === 'dob' ? 'Enter a valid date before today.' : 'Enter a valid date after today.'
      this.$set(this.dateInputErrors, field, message)
      this.$set(this.fieldErrors, field, message)
    },
    required(field) {
      if (['first_name', 'last_name'].includes(field.key)) return true
      if (field.key === 'gender') return code(this.passenger.age_type) === 'adult'
      if (field.key === 'age' && code(this.passenger.age_type) === 'child') return true
      if (field.key === 'dob' && code(this.passenger.age_type) === 'child' && this.policy.dateofbirthforchild) return true
      if (field.key === 'citizenship') return Boolean(this.policy.nationality || this.policy.placeofbirth)
      return Boolean(field.policy && this.policy[field.policy])
    },
    clientLabel(client) {
      const name = client.full_name || [client.first_name, client.last_name].filter(Boolean).join(' ') || `Client #${client.id}`
      return [name, label(client.age_type) || '—', empty(client.age) ? '—' : `${client.age} years`].join(' · ')
    },
    clientSelectable(client, slot) {
      return code(client.age_type) === code(slot.age_type) && !empty(client.age) && !this.passengers.some(person => person.id !== slot.id && this.assignments[person.id] === client.id)
    },
    personLabel(person) { return `${passengerName(person, false, false)} · #${person.id}` },
    initialize() {
      this.opener = document.activeElement
      this.fieldErrors = {}
      this.dateInputErrors = {}
      this.saving = false
      this.assignments = Object.fromEntries(this.passengers.map(person => [person.id, person.client_id === undefined ? null : person.client_id]))
      this.draft = this.passenger ? passengerPayload(this.passenger) : {}
      this.loadOptions()
    },
    async loadOptions() {
      if (!this.passenger && !this.canLinkClients) {
        this.loading = false
        return
      }
      const sessionId = ++this.sessionId
      this.loading = true
      this.loadError = ''
      try {
        if (this.passenger) {
          const [genders, countries] = await Promise.all([getGenders({ localErrors: true }), getCountries({ localErrors: true })])
          if (sessionId !== this.sessionId) return
          this.genders = rows(genders.data)
          this.countries = rows(countries.data)
        } else {
          const { data } = await getFlightsBookingClients(this.booking.id, { localErrors: true })
          if (sessionId !== this.sessionId) return
          this.clients = rows(data)
        }
      } catch (error) {
        if (sessionId === this.sessionId) this.loadError = responseError(error, 'Unable to load passenger options')
      } finally {
        if (sessionId === this.sessionId) this.loading = false
      }
    },
    async save() {
      if (this.saving || this.loading || this.loadError || this.completed || this.noClients || (!this.passenger && !this.canLinkClients)) return
      this.fieldErrors = this.passenger ? { ...this.dateInputErrors, ...passengerDateErrors(this.draft) } : {}
      if (this.passenger) {
        for (const section of this.sections) {
          for (const field of section.fields) {
            if (this.required(field) && empty(this.draft[field.key])) this.$set(this.fieldErrors, field.key, 'This field is required.')
          }
        }
      }
      if (this.passenger && this.draft.phone === 0) this.$set(this.fieldErrors, 'phone', 'Invalid phone number format')
      if (Object.keys(this.fieldErrors).length) return
      this.saving = true
      const sessionId = this.sessionId
      try {
        if (this.passenger) {
          await putFlightsBookingPassenger(this.booking.id, this.passenger.id, passengerPayload(this.draft), { localErrors: true })
        } else {
          await postFlightsBookingPassengersLinkClients(this.booking.id, {
            assignments: this.passengers.map(person => ({ passenger_id: person.id, client_id: this.assignments[person.id] === undefined ? null : this.assignments[person.id] }))
          })
        }
        if (sessionId !== this.sessionId) return
        this.saving = false
        this.$modal.hide(this.name)
        this.$emit('saved')
      } catch (error) {
        if (sessionId === this.sessionId) {
          const result = passengerRequestErrors(error, this.passenger ? personalFields : [])
          this.fieldErrors = result.fields
          if (result.message) this.$notify({ type: 'error', title: result.message })
        }
      } finally {
        if (sessionId === this.sessionId) this.saving = false
      }
    },
    focusHeading() { if (this.$refs.heading) this.$refs.heading.focus() },
    close() { if (!this.saving) this.$modal.hide(this.name) },
    beforeClose(event) {
      if (this.saving) event.cancel()
      else this.sessionId++
    },
    onClosed() { if (this.opener && this.opener.isConnected) this.opener.focus() }
  }
}
</script>

<style lang="scss" scoped>
.passenger-editor { color: #20241d; }
.passenger-editor .popup__title { font-size: 20px; font-weight: 500; }
.passenger-editor__section h3 { margin: 0 0 16px; font-size: 16px; font-weight: 500; }
.passenger-editor__section + .passenger-editor__section { padding-top: 16px; border-top: 1px solid #dce0d6; }
.passenger-editor__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 20px; }
.passenger-editor__grid > * { min-width: 0; }
.passenger-editor__loading { position: relative; min-height: 120px; }
.passenger-editor__error { display: flex; flex-direction: column; align-items: center; gap: 20px; padding: 24px 16px 32px; color: #b03932; text-align: center; overflow-wrap: anywhere; }
.passenger-editor__error p { max-width: 480px; margin: 0; font-size: 15px; line-height: 1.5; }
.passenger-editor__error-icon { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 48px; height: 48px; border-radius: 50%; background: #fff7e6; color: #eb9514; }
.passenger-editor__error-icon .pi { font-size: 22px; }
.passenger-editor__notice { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 24px; padding: 14px 16px; border: 1px solid $color-border; border-radius: 8px; background: #fafafa; color: $color-text-second; font-size: 13px; line-height: 1.5; }
.passenger-editor__notice > .pi { flex-shrink: 0; margin-top: 2px; font-size: 18px; }
.passenger-editor__notice strong { display: block; margin-bottom: 4px; color: $color-text-base; font-size: 14px; font-weight: 500; }
.passenger-editor__notice p { margin: 0; }
.passenger-editor__slot { display: grid; grid-template-columns: 140px minmax(0, 1fr); align-items: start; gap: 16px; min-width: 0; padding-bottom: 20px; }
.passenger-editor__slot-identity { display: flex; align-items: flex-start; flex-direction: column; gap: 6px; }
.passenger-editor__age { display: inline-flex; padding: 3px 8px; border-radius: 4px; font-size: 13px; font-weight: 500; }
.passenger-editor__slot[data-age-type='adult'] .passenger-editor__age { color: #3265a6; background: #edf4ff; }
.passenger-editor__slot[data-age-type='child'] .passenger-editor__age { color: #3a765b; background: #edf8f2; }
.passenger-editor__slot[data-age-type='infant'] .passenger-editor__age { color: #9e477b; background: #fcf1f8; }
.passenger-editor__slot-number { color: #62685d; font-size: 13px; }
.passenger-editor__flag { flex-shrink: 0; margin-right: 7px; }
.passenger-editor__slot-select { min-width: 0; margin-bottom: 0; }
@media (max-width: 520px) { .passenger-editor__grid { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 520px) { .passenger-editor__slot { grid-template-columns: minmax(0, 1fr); gap: 10px; } }
</style>
